(() => {
  'use strict';
  if (customElements.get('pdp-media-gallery')) return;
  let modelFeatures;
  const loadModelFeatures = () => {
    if (modelFeatures) return modelFeatures;
    const stylesheet = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
    if (!document.querySelector(`link[href="${stylesheet}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = stylesheet;
      document.head.append(link);
    }
    modelFeatures = new Promise((resolve, reject) => {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: errors => errors ? reject(errors) : resolve() }]);
    }).catch(error => { modelFeatures = null; throw error; });
    return modelFeatures;
  };

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      if (!this.track || !this.slides.length) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.links = [...this.querySelectorAll('[data-gallery-link]')];
      this.previous = this.querySelector('[data-gallery-previous]');
      this.next = this.querySelector('[data-gallery-next]');
      this.counter = this.querySelector('[data-gallery-position]');
      this.controls = this.querySelector('[data-gallery-controls]');
      this.modelViewers = this.modelViewers || new Map();
      this.index = 0;
      this.previous?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      this.next?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
      this.links.forEach((link, index) => link.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.goTo(index);
      }, { signal }));
      this.track.addEventListener('keydown', event => {
        // Keep video/model controls' native keyboard behavior intact.
        if (event.target !== this.track) return;
        const target = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (target === undefined) return;
        event.preventDefault();
        this.goTo(target);
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
      }, { signal, passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, false));
        this.resizeObserver.observe(this.track);
      }
      this.update();
      if (this.controls) this.controls.hidden = false;
      // The Shopify feature loader can finish after a deferred theme script.
      window.addEventListener('load', () => this.activateModel(), { signal, once: true });
    }

    goTo(index, animate = true) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (!slide) return;
      const left = this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left - this.track.clientLeft;
      const smooth = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    }

    update() {
      const edge = this.track.getBoundingClientRect().left + this.track.clientLeft;
      let closest = Infinity;
      let index = this.index;
      this.slides.forEach((slide, position) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - edge);
        if (distance < closest) { closest = distance; index = position; }
      });
      const changed = this.index !== index;
      this.index = index;
      if (this.counter && this.counter.textContent !== String(index + 1)) this.counter.textContent = String(index + 1);
      if (this.previous) this.previous.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;
      this.links.forEach((link, position) => {
        if (position === index && !link.hasAttribute('aria-current')) link.setAttribute('aria-current', 'true');
        else if (position !== index) link.removeAttribute('aria-current');
      });
      this.slides.forEach((slide, position) => {
        const inactive = position !== index;
        if (inactive && slide.contains(document.activeElement)) this.track.focus({ preventScroll: true });
        slide.inert = inactive;
      });
      this.activateModel();
      if (changed) this.slides.forEach((slide, position) => {
        if (position === index) return;
        slide.querySelectorAll('video').forEach(video => video.pause());
        slide.querySelectorAll('model-viewer').forEach(model => this.modelViewers.get(model)?.pause());
        // Media-tag embeds support these provider commands without an extra SDK.
        slide.querySelectorAll('iframe').forEach(frame => {
          try {
            const origin = new URL(frame.src, location.href).origin;
            if (/^https:\/\/(www\.)?youtube(-nocookie)?\.com$/.test(origin)) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
            else if (origin === 'https://player.vimeo.com') frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), origin);
          } catch { /* An unsupported provider retains its native controls. */ }
        });
      });
    }

    activateModel() {
      const model = this.slides[this.index]?.querySelector('model-viewer');
      if (!model || this.modelViewers.has(model) || this.modelLoading || !window.Shopify?.loadFeatures) return;
      const lifecycle = this.controller;
      this.modelLoading = true;
      loadModelFeatures().then(() => {
        this.modelLoading = false;
        if (!this.isConnected) return;
        if (this.controller !== lifecycle) { this.activateModel(); return; }
        const activeModel = this.slides[this.index]?.querySelector('model-viewer');
        if (activeModel && !this.modelViewers.has(activeModel) && window.Shopify.ModelViewerUI) {
          this.modelViewers.set(activeModel, new window.Shopify.ModelViewerUI(activeModel));
        }
      }).catch(() => { this.modelLoading = false; });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.slides?.forEach(slide => { slide.inert = false; });
      this.modelViewers?.forEach(viewer => viewer.pause());
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.controls) this.controls.hidden = true;
    }
  }
  customElements.define('pdp-media-gallery', ProductGallery);
})();
